import { useCallback } from 'react';
import { pick } from 'ramda';
import { batch } from 'react-redux';

import * as helpers from '../helpers';
import * as actions from '../store/actions';
import { MAX_NUMERATION_FONT_SIZE, NUMERATION_WIDTH_RATIO } from '../constants';
import { useDynamicReducerSelector, useDynamicReducerDispatch } from '../store';

export const useUpdateDimensions = (container: HTMLDivElement | null) => {
    const dispatch = useDynamicReducerDispatch();
    const { itemWidth, config: { gap, itemsPerView, tailWidth, numeration, ratio } } = useDynamicReducerSelector(pick([
        'itemWidth', 'config'
    ]));

    return useCallback(({
        isOnlyOneView, reserveTailSpace, isSilent
    }: { isOnlyOneView: boolean; reserveTailSpace: boolean; isSilent: boolean }): void => {
        if (!container) {
            return;
        }

        const tailWidthPx = helpers.percentsToPx(container, tailWidth);
        const availableWidth = isOnlyOneView && !reserveTailSpace || !tailWidth
            ? container.clientWidth
            : container.clientWidth - tailWidthPx - gap;
        const width = (availableWidth - gap * (itemsPerView - 1)) / itemsPerView;

        ratio && (container.style.height = `${width * ratio}px`);

        // region numeration
        const numerationFontSize = Math.min(container.clientHeight / 2, MAX_NUMERATION_FONT_SIZE);
        const numerationWidth = numerationFontSize * NUMERATION_WIDTH_RATIO;

        if (numeration && ratio) {
            container.style.height = `${(width - numerationWidth) * ratio}px`;
        }
        // endregion

        if (itemWidth === width) {
            return;
        }

        batch(() => {
            dispatch(actions.updateItemWidth(width, isSilent));
            numeration && dispatch(actions.updateNumerationData({
                fontSize: numerationFontSize,
                width: numerationWidth
            }, isSilent));
        });
    }, [
        itemWidth, container, dispatch, gap,
        itemsPerView, tailWidth, numeration, ratio
    ]);
};
